/** #759 — latency-scaled start-confirm window for slow postMessage play_uri dispatch. */

import { isTimerBasedRenderer, type GaplessPrefetchZoneHint } from './types';

export const START_CONFIRM_WINDOW_MIN_MS = 8_000;
export const START_CONFIRM_WINDOW_CAP_MS = 30_000;
export const START_CONFIRM_WINDOW_LATENCY_MULT = 3;

export type StartConfirmSession = {
  dispatchedAt: number;
  dispatchLatencyMs: number | null;
  confirmWindowMs: number;
  evidence: Set<string>;
  rendererId: string;
  rendererName: string;
};

let lastDispatchLatencyMs: number | null = null;

export function resetStartConfirmLatencyForTests(): void {
  lastDispatchLatencyMs = null;
}

export function noteMeasuredDispatchLatencyMs(latencyMs: number): void {
  lastDispatchLatencyMs = latencyMs;
}

export function lastMeasuredDispatchLatencyMs(): number | null {
  return lastDispatchLatencyMs;
}

export function computeStartConfirmWindowMs(dispatchLatencyMs: number | null | undefined): number {
  const scaled = Math.max(0, dispatchLatencyMs ?? 0) * START_CONFIRM_WINDOW_LATENCY_MULT;
  return Math.min(
    START_CONFIRM_WINDOW_CAP_MS,
    Math.max(START_CONFIRM_WINDOW_MIN_MS, scaled),
  );
}

export function resolveInitialConfirmWindowMs(
  renderer: { id: string; name?: string } | null,
  zone: GaplessPrefetchZoneHint | null,
  dispatchLatencyMs: number | null = lastDispatchLatencyMs,
): number {
  if (dispatchLatencyMs != null) {
    return computeStartConfirmWindowMs(dispatchLatencyMs);
  }
  if (isTimerBasedRenderer(renderer, zone)) {
    return START_CONFIRM_WINDOW_CAP_MS;
  }
  return START_CONFIRM_WINDOW_MIN_MS;
}

export function refreshSessionConfirmWindowMs(
  session: StartConfirmSession,
  zone: GaplessPrefetchZoneHint | null,
): void {
  session.confirmWindowMs = resolveInitialConfirmWindowMs(
    { id: session.rendererId, name: session.rendererName },
    zone,
    session.dispatchLatencyMs,
  );
}

export function hasStartConfirmPositionProgress(session: StartConfirmSession | null): boolean {
  return !!session?.evidence.has('position_gt_zero');
}

export function isStartConfirmGraceActive(
  session: StartConfirmSession | null,
  hasAcceptanceEvidence: boolean,
): boolean {
  if (!session || !hasAcceptanceEvidence) return false;
  if (hasStartConfirmPositionProgress(session)) return false;
  return Date.now() - session.dispatchedAt < session.confirmWindowMs;
}

export function skipConfirmedFailureBeaconPayload(
  reason: string,
  rendererName: string,
  session: StartConfirmSession | null,
): Record<string, unknown> {
  return {
    reason,
    renderer: rendererName,
    dispatch_latency_ms: session?.dispatchLatencyMs ?? null,
    window_ms: session?.confirmWindowMs ?? computeStartConfirmWindowMs(session?.dispatchLatencyMs),
  };
}
